import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAsyncFn } from 'react-use'
import { type Asyncify, type Promisable } from 'type-fest'

import { SuspenseDialog } from '~/components/elements/SuspenseDialog'
import { VStack } from '~/components/elements/VStack'
import { useTurnOffAtom } from '~/hooks/jotai'

import { isVisibleLoadDialogAtom } from '../../atoms'
import { type InputValues, type SavedFilter } from '../../utils'

import { FilterItem, type FilterItemProps } from './FilterItem'
import styles from './LoadFilterDialog.css'

export type LoadFilterDialogProps<TFilterItem> = {
  filters: SavedFilter[]
  onDelete?: (filterId: string) => Promisable<void>
  onLoad?: (
    filterId: string,
  ) => Promisable<InputValues<TFilterItem>['filterGroups']>
}

export const LoadFilterDialog = <TFilterItem,>({
  filters,
  onDelete,
  onLoad,
}: LoadFilterDialogProps<TFilterItem>) => {
  const { t } = useTranslation()
  const { setValue } = useFormContext<InputValues<TFilterItem>>()

  const closeLoadDialog = useTurnOffAtom(isVisibleLoadDialogAtom)

  const [handleDeleteState, handleDelete] = useAsyncFn<
    Asyncify<EH<FilterItemProps, 'onSelect'>>
  >(
    async (filterId) => {
      await onDelete?.(filterId)
    },
    [onDelete],
  )

  const [handleSelectState, handleSelect] = useAsyncFn<
    Asyncify<EH<FilterItemProps, 'onSelect'>>
  >(
    async (filterId) => {
      const filterGroups = await onLoad?.(filterId)
      if (filterGroups) {
        // 期待どおりに反映されないことがあるため、一旦空にしてから再設定する
        setValue('filterGroups', [])
        setValue('filterGroups', filterGroups, { shouldDirty: true })
      }

      closeLoadDialog()
    },
    [closeLoadDialog, onLoad, setValue],
  )

  const executing = handleDeleteState.loading || handleSelectState.loading

  return (
    <SuspenseDialog
      className={styles.root}
      executing={executing}
      title={t('saved_conditions')}
      onClose={closeLoadDialog}
    >
      <VStack spacing={2}>
        {filters.map((filter) => (
          <FilterItem
            key={filter.id}
            filter={filter}
            onDelete={handleDelete}
            onSelect={handleSelect}
          />
        ))}
      </VStack>
    </SuspenseDialog>
  )
}
